<template>
  <div class="device-details-info">
    <!-- Device details section -->
    <div class="section-content">
      <div class="section-header section-header-alt">
        <div class="section-title">DEVICE DETAILS :</div>
        <div class="action-buttons-container">
          <!-- Add Radio button for MECHANICAL devices without radio -->
          <button
              class="action-button add-radio-button"
              v-if="device && device.type === 'MECHANICAL' && !hasActiveRadio"
              @click="showAddRadioOverlay"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 18h.01"></path>
              <path d="M8 6h.01"></path>
              <path d="M16 12h.01"></path>
              <path d="M12 3v18"></path>
              <path d="M3 12h18"></path>
            </svg>
            Add Radio
          </button>

          <!-- Add Meter button for ADDON devices without meter -->
          <button
              class="action-button add-meter-button"
              v-if="device && device.type === 'ADDON' && !hasActiveMeter"
              @click="showAddMeterOverlay"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 18h.01"></path>
              <path d="M8 6h.01"></path>
              <path d="M16 12h.01"></path>
              <path d="M12 3v18"></path>
              <path d="M3 12h18"></path>
            </svg>
            Add Meter
          </button>

          <!-- Uninstall Radio button for MECHANICAL devices with active radio -->
          <button
              class="action-button remove-radio-button"
              v-if="device && device.type === 'MECHANICAL' && hasActiveRadio"
              @click="showRemoveRadioOverlay"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
            </svg>
            Uninstall Radio
          </button>

          <!-- Uninstall Meter button for ADDON devices with active meter -->
          <button
              class="action-button remove-meter-button"
              v-if="device && device.type === 'ADDON' && hasActiveMeter"
              @click="showRemoveMeterOverlay"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
            </svg>
            Uninstall Meter
          </button>

          <!-- Edit button -->
          <button class="action-button" @click="showEditOverlay" v-if="!isLoading && device">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Edit
          </button>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading device details...</span>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="error-state">
        <div class="error-message">{{ error }}</div>
        <button class="retry-button" @click="$emit('retry')">Retry</button>
      </div>

      <!-- Content state -->
      <div v-else-if="device" class="device-details-grid">
        <div class="row">
          <!-- Left Column -->
          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">LDN :</div>
              <div class="device-info-value">{{ device.logical_device_number }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">TYPE :</div>
              <div class="device-info-value">{{ device.type || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">CALIBER :</div>
              <div class="device-info-value">{{ device.caliber || 'N/A' }}</div>
            </div>
            <div class="device-info-row" v-if="device.type === 'MECHANICAL'">
              <div class="device-info-label">ACTIVE RADIO :</div>
              <div class="device-info-value">
                <router-link
                    v-if="hasActiveRadio"
                    :to="`/devices/${getLastActiveRadioLDN}`"
                    class="device-link"
                >
                  {{ getLastActiveRadioLDN }}
                </router-link>
                <span v-else>N/A</span>
              </div>
            </div>
            <div class="device-info-row" v-if="device.type === 'ADDON'">
              <div class="device-info-label">ACTIVE METER :</div>
              <div class="device-info-value">
                <router-link
                    v-if="hasActiveMeter"
                    :to="`/devices/${getLastActiveMeterLDN}`"
                    class="device-link"
                >
                  {{ getLastActiveMeterLDN }}
                </router-link>
                <span v-else>N/A</span>
              </div>
            </div>
            <div class="device-info-row" v-if="device.id_radio">
              <div class="device-info-label">RADIO ID :</div>
              <div class="device-info-value">{{ device.id_radio }}</div>
            </div>
            <div class="device-info-row" v-if="device.iccid">
              <div class="device-info-label">ICCID :</div>
              <div class="device-info-value">{{ device.iccid }}</div>
            </div>
            <div class="device-info-row" v-if="device.lora_eui">
              <div class="device-info-label">LORA EUI :</div>
              <div class="device-info-value">{{ device.lora_eui }}</div>
            </div>
            <div class="device-info-row" v-if="device.removed">
              <div class="device-info-label">REMOVED DATE :</div>
              <div class="device-info-value">{{ formatDate(device.removed) }}</div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">CUSTOMER NUMBER :</div>
              <div class="device-info-value">{{ device.customer_number || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">PROTOCOL :</div>
              <div class="device-info-value">{{ device.protocol || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">INSTALLED :</div>
              <div class="device-info-value">{{ formatDate(device.installed) }}</div>
            </div>
            <div class="device-info-row" v-if="device.is_integrated !== undefined">
              <div class="device-info-label">INTEGRATED :</div>
              <div class="device-info-value" :class="{'status-yes': device.is_integrated, 'status-no': !device.is_integrated}">
                {{ device.is_integrated ? 'Yes' : 'No' }}
              </div>
            </div>
            <div class="device-info-row" v-if="device.distribution_company">
              <div class="device-info-label">DISTRIBUTION COMPANY :</div>
              <div class="device-info-value">{{ device.distribution_company }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>No device information available.</p>
        <p class="secondary-text">This could be due to a missing device identifier or the device not being registered in the system.</p>
      </div>
    </div>

    <!-- Edit overlay component -->
    <DeviceDetailsEditOverlay
        :isVisible="editOverlayVisible"
        :deviceId="device?.logical_device_number"
        @close="closeEditOverlay"
        @device-updated="onDeviceUpdated"
    />

    <!-- Add Radio overlay component -->
    <DeviceDetailsAddDeviceToRadio
        :isVisible="addRadioOverlayVisible"
        :deviceId="device?.logical_device_number"
        @close="closeAddRadioOverlay"
        @radio-attached="onRadioAttached"
    />

    <!-- Add Meter overlay component -->
    <DeviceDetailsAddMeterToRadio
        :isVisible="addMeterOverlayVisible"
        :deviceId="device?.logical_device_number"
        @close="closeAddMeterOverlay"
        @meter-attached="onMeterAttached"
    />

    <!-- Remove Radio overlay component -->
    <DeviceDetailsRemoveRadioFromMeter
        :isVisible="removeRadioOverlayVisible"
        :deviceId="device?.logical_device_number"
        :currentRadioLDN="getLastActiveRadioLDN"
        @close="closeRemoveRadioOverlay"
        @radio-uninstalled="onRadioUninstalled"
    />

    <!-- Remove Meter overlay component -->
    <DeviceDetailsRemoveMeterFromRadio
        :isVisible="removeMeterOverlayVisible"
        :deviceId="device?.logical_device_number"
        :currentMeterLDN="getLastActiveMeterLDN"
        @close="closeRemoveMeterOverlay"
        @meter-uninstalled="onMeterUninstalled"
    />
  </div>
</template>

<script>
import DeviceDetailsEditOverlay from './DeviceDetailsEditOverlay.vue';
import DeviceDetailsAddDeviceToRadio from './DeviceDetailsAddRadioToDevice.vue';
import DeviceDetailsAddMeterToRadio from './DeviceDetailsAddMeterToRadio.vue';
import DeviceDetailsRemoveRadioFromMeter from './DeviceDetailsRemoveRadioFromMeter.vue';
import DeviceDetailsRemoveMeterFromRadio from './DeviceDetailsRemoveMeterFromRadio.vue';

export default {
  name: 'DeviceDetailsInfo',
  components: {
    DeviceDetailsEditOverlay,
    DeviceDetailsAddDeviceToRadio,
    DeviceDetailsAddMeterToRadio,
    DeviceDetailsRemoveRadioFromMeter,
    DeviceDetailsRemoveMeterFromRadio
  },
  props: {
    device: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      editOverlayVisible: false,
      addRadioOverlayVisible: false,
      addMeterOverlayVisible: false,
      removeRadioOverlayVisible: false,
      removeMeterOverlayVisible: false
    };
  },
  computed: {
    hasAdditionalDetails() {
      if (!this.device) return false;
      return !!(
          this.device.id_radio ||
          this.device.lora_eui ||
          this.device.distribution_company ||
          this.device.removed
      );
    },
    hasActiveRadio() {
      if (!this.device || !this.device.last_active_radio || !this.device.last_active_radio.length) {
        return false;
      }
      return this.device.last_active_radio.some(radio =>
          !radio.to_time && !radio.totime
      );
    },
    hasActiveMeter() {
      if (!this.device || !this.device.last_active_meter || !this.device.last_active_meter.length) {
        return false;
      }
      return this.device.last_active_meter.some(meter =>
          !meter.to_time && !meter.totime
      );
    },
    getLastActiveRadioLDN() {
      if (!this.hasActiveRadio) return 'N/A';
      const currentActive = this.device.last_active_radio.find(radio =>
          !radio.to_time && !radio.totime
      );
      if (currentActive) {
        return currentActive.logical_device_number;
      }
      return 'N/A';
    },
    getLastActiveMeterLDN() {
      if (!this.hasActiveMeter) return 'N/A';
      const currentActive = this.device.last_active_meter.find(meter =>
          !meter.to_time && !meter.totime
      );
      if (currentActive && currentActive.logical_device_number) {
        return currentActive.logical_device_number;
      }
      return 'N/A';
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Error';
      }
    },
    showEditOverlay() {
      this.editOverlayVisible = true;
    },
    closeEditOverlay() {
      this.editOverlayVisible = false;
    },
    showAddRadioOverlay() {
      this.addRadioOverlayVisible = true;
    },
    closeAddRadioOverlay() {
      this.addRadioOverlayVisible = false;
    },
    showAddMeterOverlay() {
      this.addMeterOverlayVisible = true;
    },
    closeAddMeterOverlay() {
      this.addMeterOverlayVisible = false;
    },
    showRemoveRadioOverlay() {
      this.removeRadioOverlayVisible = true;
    },
    closeRemoveRadioOverlay() {
      this.removeRadioOverlayVisible = false;
    },
    showRemoveMeterOverlay() {
      this.removeMeterOverlayVisible = true;
    },
    closeRemoveMeterOverlay() {
      this.removeMeterOverlayVisible = false;
    },
    onDeviceUpdated(updatedDevice) {
      this.$emit('device-updated', updatedDevice);
    },
    onRadioAttached(targetDevice) {
      console.log(`Radio attached to target device: ${targetDevice}`);
      this.$emit('reload');
    },
    onMeterAttached(targetDevice) {
      console.log(`Meter attached to target device: ${targetDevice}`);
      this.$emit('reload');
    },
    onRadioUninstalled(updatedDevice) {
      console.log(`Radio uninstalled from device:`, updatedDevice);
      this.$emit('reload');
    },
    onMeterUninstalled(updatedDevice) {
      console.log(`Meter uninstalled from radio:`, updatedDevice);
      this.$emit('reload');
    }
  }
};
</script>

<style scoped>
.device-details-info {
  height: 100%;
}

.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.section-subtitle {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 15px;
  display: inline-block;
}

.device-details-grid {
  margin-top: 35px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.device-info-row {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.device-info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-align: left;
  text-transform: uppercase;
}

.device-info-value {
  font-size: 11px;
  font-weight: 500;
}

.status-yes {
  color: #4caf50 !important;
}

.status-no {
  color: #f44336 !important;
}

/* Loading state styles */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state styles */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Empty state styles */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
}

.secondary-text {
  font-size: 11px !important;
  opacity: 0.7;
}

/* Action button styles */
.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .section-header-alt {
    margin-top: 60px;
  }

  .device-info-row {
    margin-bottom: 15px;
  }
}

.device-link {
  color: rgba(79, 195, 247, 0.9);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}

.action-buttons-container {
  display: flex;
  flex-direction: row; /* Ensure horizontal layout */
  gap: 12px;
  align-items: center;
}
</style>